import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Button } from './button';
import { overrideComisionPct, overrideComisionSplit } from '../api/comisiones';
import { getValidPrima } from '../utils/fianzaEventos';
import { diagnoseComisionPct } from '../utils/calculations';
import { useTarifas } from '../contexts/DataContext';

const TIPO_MOV_LABELS = { adicional: 'adicional', sustituye: 'sustituye', cargo: 'cargo' };

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

const clampPct = (raw) => {
  const val = parseFloat(raw);
  if (Number.isNaN(val)) return null;
  return Math.min(100, Math.max(0, val));
};

/**
 * ComisionEditorDialog — the ONE place a fianza's comisión terms are corrected.
 *
 * Both terms live here because they answer the same question ("what do I
 * actually earn on this fianza?") and are learned at the same moment — usually
 * when the afianzadora's payout arrives:
 *   - % del agente del fiado — the base rate the comisión is computed from.
 *   - Participación — how that comisión splits with the other agent (the deal
 *     is negotiated case by case; 50/50 with an agent you know is common).
 *
 * Editing is allowed even when the comisión is already COBRADA: the payout is
 * often what reveals the real numbers, so the movement is restated in place
 * (with a trace) rather than corrected with a counter-entry.
 *
 * `split` is omitted for fianzas with no convenio participación — there is
 * nothing to divide.
 */
export function ComisionEditorDialog({
  isOpen,
  onClose,
  fianza,
  contratoId,
  movimiento,          // emisión movement, when the fianza has one booked
  participacion,       // { beneficiario, fiado } currently in effect
  showSplit = true,
  onSaved,
}) {
  const [pct, setPct] = useState('');
  const [beneficiario, setBeneficiario] = useState('');
  const [fiado, setFiado] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { tarifas } = useTarifas();

  // Why the movement has no % — and, when the tables can price it today, the
  // % they produce (pre-filled below).
  const diagnostico = movimiento?.comisionPct == null && movimiento
    ? diagnoseComisionPct(movimiento, tarifas)
    : null;

  useEffect(() => {
    if (!isOpen) return;
    const pctActual = movimiento?.comisionPct ?? fianza?.comisionPct;
    setPct(
      pctActual != null ? String(pctActual)
        : diagnostico?.sugerido != null ? String(diagnostico.sugerido)
        : ''
    );
    setBeneficiario(participacion?.beneficiario != null ? String(participacion.beneficiario) : '');
    setFiado(participacion?.fiado != null ? String(participacion.fiado) : '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, movimiento?.comisionPct, fianza?.comisionPct, participacion?.beneficiario, participacion?.fiado]);

  if (!isOpen || !fianza) return null;

  const pctOriginal = movimiento?.comisionPct ?? fianza?.comisionPct ?? null;
  const splitOriginal = participacion || null;
  const pctNum = clampPct(pct);
  const benNum = clampPct(beneficiario);
  const fiaNum = clampPct(fiado);

  const pctCambio = pctNum != null && pctNum !== pctOriginal;
  const splitCambio =
    showSplit && benNum != null && fiaNum != null && splitOriginal != null &&
    (benNum !== splitOriginal.beneficiario || fiaNum !== splitOriginal.fiado);

  // Live preview off the movement's own prima, so the owner sees the money
  // before committing. Without a movement there is nothing booked to preview.
  const prima = Number(movimiento?.prima) || 0;
  const factor = showSplit && movimiento?.splitRol != null
    ? (movimiento.splitRol === 'convenio' ? (benNum ?? 0) : (fiaNum ?? 0)) / 100
    : 1;
  const previewBruta = pctNum != null ? (prima * pctNum) / 100 : null;
  const previewNeta = previewBruta == null ? null : previewBruta * factor;
  const yaCobrada = movimiento?.estadoCobro === 'cobrada';

  const complementar = (campo, raw) => {
    const val = clampPct(raw);
    if (val == null) return;
    const otro = Math.round((100 - val) * 100) / 100;
    if (campo === 'beneficiario') {
      setBeneficiario(String(val));
      setFiado(String(otro));
    } else {
      setFiado(String(val));
      setBeneficiario(String(otro));
    }
  };

  const guardar = async () => {
    if (!pctCambio && !splitCambio) {
      onClose?.();
      return;
    }
    if (pctCambio && pctNum == null) {
      setError('El porcentaje de comisión debe ser un número entre 0 y 100.');
      return;
    }
    if (splitCambio) {
      if (benNum == null || fiaNum == null) {
        setError('Los porcentajes de participación deben estar entre 0 y 100.');
        return;
      }
      if (benNum + fiaNum > 100) {
        setError('La suma de la participación no puede exceder 100.');
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      // Split first: the % override recomputes amounts through the movement's
      // stored split, so applying the new split first keeps both consistent
      // regardless of which one changed.
      if (splitCambio) {
        await overrideComisionSplit({
          contractId: contratoId,
          fianzaId: fianza.id,
          beneficiario: benNum,
          fiado: fiaNum,
        });
      }
      if (pctCambio) {
        if (!movimiento?.id) {
          throw new Error('Esta fianza aún no tiene comisión registrada; no hay % que corregir.');
        }
        await overrideComisionPct(movimiento.id, pctNum);
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error('comision editor save failed', e);
      setError(e.message || 'No se pudo guardar la comisión.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full rounded-lg border border-azul-surelia/30 px-3 py-2 pr-7 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-azul-surelia/40 focus:border-azul-surelia';

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => !saving && onClose?.()}
      />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-100">
          <h3 className="text-base font-semibold text-azul-surelia">
            Comisión de la fianza{fianza.numeroFianza ? ` #${fianza.numeroFianza}` : ''}
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            Corrige el % y la participación cuando conozcas los números reales.
          </p>
        </div>

        <div className="px-5 py-4 space-y-3">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          {yaCobrada && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              Esta comisión ya está marcada como cobrada. Al guardar se corrige el
              monto registrado para reflejar lo que realmente pagó la afianzadora.
            </div>
          )}

          {/* Diagnóstico: why there is no % — cause first, then the pricing
              facts the resolution ran on. CALCULABLE (tables price it today,
              input pre-filled) reads as good news, everything else as amber. */}
          {diagnostico && (
            <div
              className={`rounded-xl border p-4 ${
                diagnostico.code === 'CALCULABLE'
                  ? 'border-azul-surelia/25 bg-azul-surelia/5'
                  : 'border-amber-200 bg-amber-50'
              }`}
            >
              <div className="flex items-start gap-2.5">
                {diagnostico.code === 'CALCULABLE' ? (
                  <SparklesIcon className="w-4 h-4 mt-0.5 shrink-0 text-azul-surelia" />
                ) : (
                  <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                )}
                <div className="min-w-0">
                  <div
                    className={`text-sm font-medium ${
                      diagnostico.code === 'CALCULABLE' ? 'text-azul-surelia' : 'text-amber-800'
                    }`}
                  >
                    {diagnostico.code === 'CALCULABLE' ? 'Ya se puede calcular' : 'Sin % de comisión'}
                  </div>
                  <p
                    className={`mt-0.5 text-xs leading-snug ${
                      diagnostico.code === 'CALCULABLE' ? 'text-azul-surelia/80' : 'text-amber-700'
                    }`}
                  >
                    {diagnostico.mensaje}
                  </p>
                </div>
              </div>
              <dl className="mt-3 grid grid-cols-3 gap-2 border-t border-black/5 pt-3">
                <div className="min-w-0">
                  <dt className="text-[10px] uppercase tracking-wide text-zinc-400">Afianzadora</dt>
                  <dd className="text-xs text-zinc-700 truncate" title={movimiento?.afianzadora || ''}>
                    {movimiento?.afianzadora || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-wide text-zinc-400">Prima</dt>
                  <dd className="text-xs text-zinc-700 tabular-nums">
                    {movimiento?.prima ? fmtMXN(movimiento.prima) : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-wide text-zinc-400">Tarifa</dt>
                  <dd className="text-xs text-zinc-700 tabular-nums">
                    {movimiento?.tarifa != null ? `${Number(movimiento.tarifa).toFixed(2)}%` : '—'}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {/* % de comisión — the fiado agent's on the convenio side, the org's own otherwise */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-800">% de comisión</div>
                {/* Only on the convenio side is the % someone else's deal. On a
                    propia fianza (or fiado_externo, where the org IS the fiado's
                    agent) it is the org's own tarifa. */}
                {movimiento?.splitRol === 'convenio' && (
                  <div className="text-xs text-zinc-500">
                    Tarifa del agente del fiado con la afianzadora
                  </div>
                )}
              </div>
              <div className="relative w-28 shrink-0">
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  value={pct}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => { setPct(e.target.value); setError(null); }}
                  onBlur={() => { const v = clampPct(pct); if (v != null) setPct(String(v)); }}
                  onKeyDown={(e) => { if (e.key === 'Escape') onClose?.(); }}
                  className={inputCls}
                  placeholder="—"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none">%</span>
              </div>
            </div>
            {movimiento?.pctEstimado && !pctCambio && (
              <div className="mt-2 text-[11px] text-amber-700">
                Valor estimado con la tabla de comisiones por defecto — confírmalo al ver el % real.
              </div>
            )}
          </div>

          {/* Participación del convenio */}
          {showSplit && splitOriginal && (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-sm font-medium text-gray-800">Participación</div>
              <div className="text-xs text-zinc-500 mb-3">
                Cómo se reparte la comisión con el otro agente en esta fianza
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-xs font-medium text-zinc-600 mb-1">Convenio (beneficiario)</span>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={beneficiario}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => { setBeneficiario(e.target.value); setError(null); }}
                      onBlur={(e) => complementar('beneficiario', e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') onClose?.(); }}
                      className={inputCls}
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none">%</span>
                  </div>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-zinc-600 mb-1">Agente del fiado</span>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={fiado}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => { setFiado(e.target.value); setError(null); }}
                      onBlur={(e) => complementar('fiado', e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') onClose?.(); }}
                      className={inputCls}
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none">%</span>
                  </div>
                </label>
              </div>
              {benNum != null && fiaNum != null && (
                <div className="mt-3">
                  <div className="w-full h-2 rounded-full overflow-hidden flex bg-gray-100">
                    <div className="bg-azul-surelia" style={{ width: `${benNum}%` }} />
                    <div className="bg-cian-digital" style={{ width: `${fiaNum}%` }} />
                  </div>
                  <div className="mt-1 flex justify-between text-[10px] text-zinc-400">
                    <span>Convenio {benNum}%</span>
                    <span>Agente del fiado {fiaNum}%</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview */}
          {movimiento && previewNeta != null && (
            <div className="rounded-xl border border-azul-surelia/20 bg-azul-surelia/5 px-4 py-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-600">
                  Prima{movimiento.tipo !== 'emision' && TIPO_MOV_LABELS[movimiento.tipo]
                    ? ` (${TIPO_MOV_LABELS[movimiento.tipo]})`
                    : ''} {fmtMXN(prima)} × {pctNum}%
                  {factor !== 1 && ` × ${Math.round(factor * 100)}%`}
                </span>
                <span className="font-bold text-azul-surelia tabular-nums">{fmtMXN(previewNeta)}</span>
              </div>
              {factor !== 1 && previewBruta != null && (
                <div className="text-[11px] text-zinc-400 text-right">{fmtMXN(previewBruta)} bruta</div>
              )}
              {movimiento.comisionMonto != null &&
                Math.abs((Number(movimiento.comisionMonto) || 0) - previewNeta) > 0.005 && (
                <div className="mt-1 text-[11px] text-zinc-500 text-right">
                  antes {fmtMXN(movimiento.comisionMonto)}
                </div>
              )}
              {movimiento.tipo !== 'emision' && (() => {
                const primaFianza = getValidPrima(fianza);
                if (!primaFianza || primaFianza === prima) return null;
                return (
                  <div className="mt-2 pt-2 border-t border-azul-surelia/10">
                    <div className="flex items-center justify-between text-[11px] text-zinc-500">
                      <span>
                        Fianza completa {fmtMXN(primaFianza)} × {pctNum}%
                        {factor !== 1 && ` × ${Math.round(factor * 100)}%`}
                      </span>
                      <span className="tabular-nums">
                        {fmtMXN((primaFianza * pctNum) / 100 * factor)}
                      </span>
                    </div>
                    <div className="text-[10px] text-zinc-400 mt-0.5">
                      El % queda fijado para toda la fianza; la emisión aún no está en el libro.
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {!movimiento && (
            <div className="text-xs text-zinc-500">
              Esta fianza aún no tiene comisión registrada en el libro; solo puedes
              ajustar la participación.
            </div>
          )}
        </div>

        <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 flex justify-end gap-2">
          <Button plain onClick={() => onClose?.()} disabled={saving}>Cancelar</Button>
          <Button color="surelia" onClick={guardar} disabled={saving || (!pctCambio && !splitCambio)}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ComisionEditorDialog;
